import { useCallback, useEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { rqlBuilder } from "@extrahorizon/javascript-sdk";

import { getExhClient, isExhClientInitialized } from "lib/extrahorizon/client";
import { ExhMeasurementWbcResult } from "types/measurement";
import { ExhPatientData, PatientResult, PatientsListEntry } from "types/patient";

import { convertExhWbcResultToPatientResult } from "../utils/measurementConverters";
import { convertToPatientListEntry } from "../utils/patientConverters";

interface UsePatientsResult {
  patients: PatientsListEntry[];
  loading: boolean;
  refetch: () => Promise<void>;
}

export const usePatients = (): UsePatientsResult => {
  const [patients, setPatients] = useState<PatientsListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const { showBoundary: onError } = useErrorBoundary();

  const loadPatients = useCallback(async () => {
    if (!isExhClientInitialized()) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const client = getExhClient();
      // This will be intercepted and redirected to patient-endpoint
      const result = await client.data.documents.find("patient", {
        rql: "?limit(100)",
      });
      const rawPatients: ExhPatientData[] = (result.data ?? []) as ExhPatientData[];

      // Batch-fetch the latest wbc result for each patient in one query
      const allResultIds = rawPatients
        .map((p) => p.data?.last_measurement_result_id)
        .filter((id): id is string => Boolean(id));

      let resultById = new Map<string, PatientResult>();
      if (allResultIds.length > 0) {
        try {
          const rql = rqlBuilder().in("id", allResultIds).build();
          const wbcResponse = await client.data.documents.find("measurement-wbc-result", { rql });
          const wbcDocs = (wbcResponse.data ?? []) as ExhMeasurementWbcResult[];
          resultById = new Map(wbcDocs.map((doc) => [doc.id, convertExhWbcResultToPatientResult(doc)]));
        } catch {
          // Batch fetch failed; ResultCard falls back to per-patient hook for affected patients
        }
      }

      const entries = rawPatients.map((p) => {
        const entry = convertToPatientListEntry(p);
        const resultId = p.data?.last_measurement_result_id;
        return {
          ...entry,
          latestResult: resultId ? (resultById.get(resultId) ?? null) : null,
        };
      });

      setPatients(entries);
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  return { patients, loading, refetch: loadPatients };
};
